import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import '../styles/productPage.css';
import aiWorkflowDark from '../assets/ai-investigation-workflow.svg';
import aiWorkflowLight from '../assets/ai-investigation-workflow-light.svg';

const TABS = ['overview', 'analytics', 'caseManagement', 'compliance', 'taskManagement'];

const ANALYTICS_KEYS = [
  'anomalyDetection', 'trendAnalysis', 'dataCorrelation', 'alertEngine',
  'taskForce', 'chainOfCommand', 'autoAssign', 'reportsDashboard',
  'universalSearch',
];

const HIGHLIGHT_ICONS = {
  detect: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  case: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
    </svg>
  ),
  audit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  report: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ),
};

const AIInvestigation = () => {
  const { t } = useTranslation('aiInvestigation');
  const { language } = useLanguage();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const tabsSectionRef = useRef(null);
  const aiWorkflowSvg = theme === 'light' ? aiWorkflowLight : aiWorkflowDark;

  const isLight = theme === 'light';
  const commandOverviewImg = isLight ? '/Images/CommandOverview-light.png' : '/Images/CommandOverview.png';
  const investigationImg = isLight
    ? '/Images/suspect-network-diagram-light.png'
    : '/Images/suspect-network-diagram.jpg';

  const goToTab = (index) => {
    setActiveTab(index);
    requestAnimationFrame(() => {
      if (tabsSectionRef.current) {
        const top = tabsSectionRef.current.offsetTop - 105;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  };

  return (
    <div className="product-page">
      <SEO
        title="E1 Analytics, Big Data Predictive Intelligence and Case Tracking"
        description="EWANDS E1 Analytics applies big data processing, predictive modelling, pattern recognition, and anomaly detection to operational datasets. Built-in case tracking and compliance reporting help teams move from raw data to decisions with full auditability across every step."
        keywords="AI analytics Malaysia, big data analytics AI, predictive analytics enterprise, pattern recognition AI, anomaly detection, case tracking system, operational intelligence, compliance analytics AI, enterprise data analytics Malaysia, AI predictive modelling"
        canonicalPath={`/${language}/e1-analytics`}
      />

      {/* Hero */}
      <section className="product-hero">
        <div className="container">
          <div className="product-hero-inner">
            <span className="product-hero-tag">{t('overview.tag')}</span>
            <h1>{t('hero.title')}</h1>
            <p className="product-hero-subtitle">{t('hero.subtitle')}</p>
            <p className="product-hero-desc">{t('hero.description')}</p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="product-tabs-section" ref={tabsSectionRef}>
        <div className="product-tab-bar-wrapper">
          <div className="container">
            <div className="product-tab-bar">
              {TABS.map((tab, i) => (
                <button
                  key={tab}
                  className={`product-tab-btn${activeTab === i ? ' active' : ''}`}
                  onClick={() => goToTab(i)}
                >
                  {t(`tabs.${tab}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="product-tab-content">
          <div className="container">

            {/* ── Overview ───────────────────────────────── */}
            {activeTab === 0 && (
              <div>
                <span className="pp-section-tag">{t('overview.tag')}</span>
                <h2 className="pp-section-heading">{t('overview.heading')}</h2>
                <p className="pp-section-desc">{t('overview.description')}</p>

                <div className="pp-overview-highlights">
                  {[
                    { k: 'h1', icon: HIGHLIGHT_ICONS.detect },
                    { k: 'h2', icon: HIGHLIGHT_ICONS.case },
                    { k: 'h3', icon: HIGHLIGHT_ICONS.audit },
                    { k: 'h4', icon: HIGHLIGHT_ICONS.report },
                  ].map(({ k, icon }) => (
                    <div className="pp-overview-card" key={k}>
                      <div className="pp-overview-card-icon">{icon}</div>
                      <div className="pp-overview-card-label">{t(`overview.highlights.${k}.label`)}</div>
                      <div className="pp-overview-card-desc">{t(`overview.highlights.${k}.desc`)}</div>
                    </div>
                  ))}
                </div>

                <div className="row g-4 mt-2">
                  <div className="col-lg-6">
                    <img src={commandOverviewImg} alt={t('overview.imgAlt1')} className="product-overview-img" />
                  </div>
                  <div className="col-lg-6">
                    <img src={investigationImg} alt={t('overview.imgAlt2')} className="product-overview-img" />
                  </div>
                </div>
              </div>
            )}

            {/* ── Analytics ──────────────────────────────── */}
            {activeTab === 1 && (
              <div>
                <span className="pp-section-tag">{t('analytics.tag')}</span>
                <h2 className="pp-section-heading">{t('analytics.heading')}</h2>
                <p className="pp-section-desc">{t('analytics.subheading')}</p>

                <div className="row g-4 mb-5">
                  <div className="col-12">
                    <img
                      src={aiWorkflowSvg}
                      alt={t('analytics.imgAlt1')}
                      style={{ width: '100%', borderRadius: '12px', display: 'block' }}
                    />
                  </div>
                </div>

                <div className="pp-feature-grid">
                  {ANALYTICS_KEYS.map(key => (
                    <div
                      className={`pp-feature-card${key === 'universalSearch' ? ' pp-feature-card--wide' : ''}`}
                      key={key}
                    >
                      <span className="pp-feature-tag">{t(`analytics.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`analytics.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`analytics.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Case Management ────────────────────────── */}
            {activeTab === 2 && (
              <div>
                <span className="pp-section-tag">{t('caseManagement.tag')}</span>
                <h2 className="pp-section-heading">{t('caseManagement.heading')}</h2>

                <div className="pp-feature-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  {['caseCreation','evidenceTagging','auditTrail','escalation'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`caseManagement.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`caseManagement.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`caseManagement.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Compliance ─────────────────────────────── */}
            {activeTab === 3 && (
              <div>
                <span className="pp-section-tag">{t('compliance.tag')}</span>
                <h2 className="pp-section-heading">{t('compliance.heading')}</h2>

                <div className="pp-feature-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  {['regulatoryReporting','auditExport','chainOfCustody','retentionPolicy'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`compliance.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`compliance.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`compliance.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Task Management ────────────────────────── */}
            {activeTab === 4 && (
              <div>
                <span className="pp-section-tag">{t('taskManagement.tag')}</span>
                <h2 className="pp-section-heading">{t('taskManagement.heading')}</h2>

                <div className="pp-feature-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  {['campaignPlanning','externalCollaboration','progressTracking','operationClose'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`taskManagement.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`taskManagement.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`taskManagement.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Tab Navigation ─────────────────────────── */}
            <div className="pp-tab-nav">
              {activeTab > 0 ? (
                <button className="pp-tab-nav-btn pp-tab-nav-prev" onClick={() => goToTab(activeTab - 1)}>
                  <span className="pp-tab-nav-dir">← Prev</span>
                  <span className="pp-tab-nav-label">{t(`tabs.${TABS[activeTab - 1]}`)}</span>
                </button>
              ) : <span />}
              {activeTab < TABS.length - 1 ? (
                <button className="pp-tab-nav-btn pp-tab-nav-next" onClick={() => goToTab(activeTab + 1)}>
                  <span className="pp-tab-nav-dir">Next →</span>
                  <span className="pp-tab-nav-label">{t(`tabs.${TABS[activeTab + 1]}`)}</span>
                </button>
              ) : <span />}
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default AIInvestigation;
